// TMOS Admin Notification Service
// Date: November 15, 2025
// Purpose: Frontend service for admin notifications API

import api from './api';

export interface AdminNotification {
  id: string;
  notification_type: 'SYSTEM_ANNOUNCEMENT' | 'FEATURE_UPDATE' | 'MAINTENANCE_NOTICE' | 'GENERAL';
  title: string;
  message: string;
  is_active: boolean;
  scheduled_start_time?: string | null;
  scheduled_end_time?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAdminNotificationRequest {
  notification_type: 'SYSTEM_ANNOUNCEMENT' | 'FEATURE_UPDATE' | 'MAINTENANCE_NOTICE' | 'GENERAL';
  title: string;
  message: string;
  is_active?: boolean;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
}

export interface UpdateAdminNotificationRequest {
  notification_type?: 'SYSTEM_ANNOUNCEMENT' | 'FEATURE_UPDATE' | 'MAINTENANCE_NOTICE' | 'GENERAL';
  title?: string;
  message?: string;
  is_active?: boolean;
  scheduled_start_time?: string | null;
  scheduled_end_time?: string | null;
}

export interface AdminNotificationView {
  id: string;
  notification_id: string;
  user_id: string;
  view_count: number;
  first_viewed_at?: string | null;
  last_viewed_at?: string | null;
  dismissed_at?: string | null;
  created_at: string;
}

class AdminNotificationService {
  /**
   * Get active notifications for current user
   */
  async getActiveNotifications(): Promise<AdminNotification[]> {
    const response = await api.get('/notifications/active');
    return response.data.data || [];
  }

  /**
   * Record notification view
   */
  async recordView(notificationId: string): Promise<void> {
    await api.post(`/notifications/${notificationId}/view`);
  }

  /**
   * Dismiss notification
   */
  async dismissNotification(notificationId: string): Promise<void> {
    await api.post(`/notifications/${notificationId}/dismiss`);
  }

  /**
   * Get all notifications (admin only)
   */
  async getAllNotifications(): Promise<AdminNotification[]> {
    const response = await api.get('/notifications/admin');
    return response.data.data || [];
  }

  /**
   * Get notification by ID (admin only)
   */
  async getNotificationById(id: string): Promise<AdminNotification> {
    const response = await api.get(`/notifications/admin/${id}`);
    return response.data.data;
  }

  /**
   * Create notification (admin only)
   */
  async createNotification(data: CreateAdminNotificationRequest): Promise<AdminNotification> {
    const response = await api.post('/notifications/admin', data);
    return response.data.data;
  }

  /**
   * Update notification (admin only)
   */
  async updateNotification(id: string, data: UpdateAdminNotificationRequest): Promise<AdminNotification> {
    const response = await api.put(`/notifications/admin/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete notification (admin only)
   */
  async deleteNotification(id: string): Promise<void> {
    await api.delete(`/notifications/admin/${id}`);
  }

  /**
   * Update scheduled notifications status (admin only)
   */
  async updateScheduledNotifications(): Promise<void> {
    await api.post('/notifications/admin/update-scheduled');
  }
}

export const adminNotificationService = new AdminNotificationService();

